import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `class Autocomplete {
  /** Add a phrase. Adding the same one twice changes nothing. */
  insert(phrase: string): void {
    // TODO
  }

  /** Is this exact phrase in the index? */
  has(phrase: string): boolean {
    // TODO
    return false;
  }

  /** Does any phrase start with this? */
  startsWith(prefix: string): boolean {
    // TODO
    return false;
  }

  /** Up to \`limit\` phrases starting with \`prefix\`, in ascending order. */
  suggest(prefix: string, limit: number): string[] {
    // TODO
    return [];
  }
}
`;

const solution = `type Node = { children: Map<string, Node>; terminal: boolean };

const node = (): Node => ({ children: new Map(), terminal: false });

class Autocomplete {
  private root: Node = node();

  insert(phrase: string): void {
    let current = this.root;
    for (const ch of phrase) {
      let next = current.children.get(ch);
      if (!next) {
        next = node();
        current.children.set(ch, next);
      }
      current = next;
    }
    // The flag is what separates a phrase from a path through one. Without it,
    // "app" would exist the moment somebody indexed "apple".
    current.terminal = true;
  }

  /** Walk the prefix and return where it lands, or null if the path runs out. */
  private descend(prefix: string): Node | null {
    let current = this.root;
    for (const ch of prefix) {
      const next = current.children.get(ch);
      if (!next) return null;
      current = next;
    }
    return current;
  }

  has(phrase: string): boolean {
    const found = this.descend(phrase);
    return found !== null && found.terminal;
  }

  startsWith(prefix: string): boolean {
    return this.descend(prefix) !== null;
  }

  suggest(prefix: string, limit: number): string[] {
    const out: string[] = [];
    if (limit <= 0) return out;

    const start = this.descend(prefix);
    if (!start) return out;

    // Depth first with the children visited in key order, which is why the
    // results come out sorted without a sort at the end. Stopping the instant
    // limit is reached is the point of the whole structure: a prefix with ten
    // thousand completions still costs the ten you asked for.
    const walk = (current: Node, built: string): void => {
      if (out.length >= limit) return;
      if (current.terminal) {
        out.push(built);
        if (out.length >= limit) return;
      }
      for (const ch of [...current.children.keys()].sort()) {
        walk(current.children.get(ch)!, built + ch);
        if (out.length >= limit) return;
      }
    };

    walk(start, prefix);
    return out;
  }
}`;

const harness = HARNESS_KIT + `
if (typeof Autocomplete !== "function") {
  throw new Error("Autocomplete is not defined. Keep the class name as given.");
}

function __index(phrases) {
  var a = new Autocomplete();
  for (var i = 0; i < phrases.length; i++) a.insert(phrases[i]);
  return a;
}

var __FRUIT = ["apple", "apply", "app", "apricot", "banana"];

const __tests = [
  {
    name: "suggests the completions of a prefix, in order",
    about: "The basic contract.",
    run: function () {
      var a = __index(__FRUIT);
      __expectExact('suggest("ap", 10)', function () { return a.suggest("ap", 10); },
        ["app", "apple", "apply", "apricot"]);
    }
  },
  {
    name: "has is exact, startsWith is a prefix",
    about: "The distinction the whole structure exists for. Indexing 'apple' does not put 'app' in the index, but it does mean something starts with it.",
    run: function () {
      var a = __index(["apple"]);
      __expectExact('has("apple")', function () { return a.has("apple"); }, true);
      __expectExact('has("app")', function () { return a.has("app"); }, false);
      __expectExact('startsWith("app")', function () { return a.startsWith("app"); }, true);
      __expectExact('startsWith("apple")', function () { return a.startsWith("apple"); }, true);
    }
  },
  {
    name: "limit caps the number of suggestions",
    about: "A dropdown shows five rows. Returning four thousand and letting the caller slice defeats the point of asking.",
    run: function () {
      var a = __index(__FRUIT);
      __expectExact('suggest("ap", 2)', function () { return a.suggest("ap", 2); }, ["app", "apple"]);
    }
  },
  {
    name: "a prefix that is itself a phrase is one of its own suggestions",
    hidden: true,
    about: "Typing the whole word must not make it vanish from the list. Starting the walk below the prefix node skips it.",
    run: function () {
      var a = __index(["app", "apple"]);
      __expectExact('suggest("app", 5)', function () { return a.suggest("app", 5); }, ["app", "apple"]);
    }
  },
  {
    name: "an unknown prefix suggests nothing",
    hidden: true,
    about: "Running off the end of the trie is an empty answer, not a crash and not everything.",
    run: function () {
      var a = __index(__FRUIT);
      __expectExact('suggest("zz", 5)', function () { return a.suggest("zz", 5); }, []);
      __expectExact('startsWith("zz")', function () { return a.startsWith("zz"); }, false);
      __expectExact('suggest("applesauce", 5)', function () { return a.suggest("applesauce", 5); }, []);
    }
  },
  {
    name: "inserting the same phrase twice does not duplicate it",
    hidden: true,
    about: "Re-indexing is normal. A phrase appearing twice in a dropdown is the visible symptom of counting instead of marking.",
    run: function () {
      var a = __index(["apple", "apple", "apple"]);
      __expectExact('suggest("a", 5)', function () { return a.suggest("a", 5); }, ["apple"]);
    }
  },
  {
    name: "an empty prefix means everything",
    hidden: true,
    about: "An empty search box asking for the first few entries is a real query, and descending on an empty string must land on the root rather than fail.",
    run: function () {
      var a = __index(__FRUIT);
      __expectExact('suggest("", 3)', function () { return a.suggest("", 3); }, ["app", "apple", "apply"]);
      __expectExact('suggest("", 10)', function () { return a.suggest("", 10); },
        ["app", "apple", "apply", "apricot", "banana"]);
    }
  },
  {
    name: "a limit of zero or less returns nothing",
    hidden: true,
    about: "A degenerate limit should return nothing rather than everything, which is what a loop guarded only by 'not yet at the limit' does.",
    run: function () {
      var a = __index(__FRUIT);
      __expectExact('suggest("ap", 0)', function () { return a.suggest("ap", 0); }, []);
      __expectExact('suggest("ap", -1)', function () { return a.suggest("ap", -1); }, []);
    }
  },
  {
    name: "order does not depend on insertion order",
    hidden: true,
    about: "Suggestions have to be stable between sessions. A Map preserves insertion order, so walking its keys as they come out reflects whatever order the index happened to be built in.",
    run: function () {
      var a = __index(["zeta", "alpha", "beta"]);
      var b = __index(["beta", "zeta", "alpha"]);
      __expectExact("built in one order", function () { return a.suggest("", 5); }, ["alpha", "beta", "zeta"]);
      __expectExact("built in another", function () { return b.suggest("", 5); }, ["alpha", "beta", "zeta"]);
    }
  },
  {
    name: "matching is case sensitive, and upper case sorts first",
    hidden: true,
    about: "Ticker symbols and product names are capitalised on purpose. Lower-casing everything merges entries that are genuinely different.",
    run: function () {
      var a = __index(["Apple", "apple"]);
      __expectExact('has("Apple")', function () { return a.has("Apple"); }, true);
      __expectExact('suggest("A", 5)', function () { return a.suggest("A", 5); }, ["Apple"]);
      __expectExact('suggest("", 5)', function () { return a.suggest("", 5); }, ["Apple", "apple"]);
    }
  },
  {
    name: "an empty phrase can be indexed",
    hidden: true,
    about: "The root is a node like any other. Special-casing the empty string is how a trie ends up with two different notions of what exists.",
    run: function () {
      var a = __index(["", "a"]);
      __expectExact('has("")', function () { return a.has(""); }, true);
      __expectExact('suggest("", 5)', function () { return a.suggest("", 5); }, ["", "a"]);
      __expectExact("an untouched index has no empty phrase",
        function () { return new Autocomplete().has(""); }, false);
    }
  },
  {
    name: "correct across a large index",
    hidden: true,
    about: "Scale is where the traversal stops agreeing with a filter over every phrase, which is the only reason to build a trie at all.",
    run: function () {
      var a = new Autocomplete();
      var all = [];
      for (var i = 0; i < 400; i++) {
        var word = "term" + String(i).padStart(3, "0");
        all.push(word);
        a.insert(word);
      }
      var expected = all.filter(function (w) { return w.indexOf("term01") === 0; }).sort().slice(0, 4);
      __expectExact('suggest("term01", 4)', function () { return a.suggest("term01", 4); }, expected);
      __expectExact('has("term399")', function () { return a.has("term399"); }, true);
      __expectExact('has("term400")', function () { return a.has("term400"); }, false);
      __expectExact('suggest("term", 3)', function () { return a.suggest("term", 3); },
        ["term000", "term001", "term002"]);
    }
  },
  {
    name: "a generated index, checked against filter and sort",
    hidden: true,
    about: "Random phrases and a random prefix, with the answer worked out a second time by filtering every phrase and sorting the survivors. The trie has to agree with the obvious version. The index is different the next time this drill comes round, so passing it means the traversal is right rather than remembered.",
    run: function () {
      const g = __gen("index");
      const alphabet = "abcd".split("");
      const words = g.array(g.int(20, 40),
        function () { return g.str(g.int(1, 5), alphabet); }, { distinct: true });
      const a = new Autocomplete();
      // Inserted in a shuffled order, because a trie built from sorted input can
      // come out sorted by accident.
      for (const w of g.shuffle(words)) a.insert(w);

      const prefix = g.bool() ? g.str(1, alphabet) : g.str(2, alphabet);
      const limit = g.int(1, 5);
      __expectOracle('suggest(' + __show(prefix) + ", " + limit + ") over a generated index",
        function () { return a.suggest(prefix, limit); },
        function () {
          return words.filter(function (w) { return w.indexOf(prefix) === 0; }).sort().slice(0, limit);
        });

      const present = g.pick(words);
      __expectExact("has(" + __show(present) + "), which was indexed",
        function () { return a.has(present); }, true);

      // A phrase built from letters outside the alphabet cannot have been
      // indexed, so this is a real negative rather than a lucky one.
      __expectExact('has("zz"), which was not indexed',
        function () { return a.has("zz"); }, false);

      __expectOracle("startsWith(" + __show(prefix) + ")",
        function () { return a.startsWith(prefix); },
        function () {
          return words.some(function (w) { return w.indexOf(prefix) === 0; });
        });
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "Ask what makes has(\"app\") false while startsWith(\"app\") is true, when the only thing indexed was apple. One flag per node is the whole answer, and it is what separates a phrase from a path through one.",
    code: `type Node = { children: Map<string, Node>; terminal: boolean };

const node = (): Node => ({ children: new Map(), terminal: false });

insert(phrase: string): void {
  let current = this.root;
  for (const ch of phrase) {
    let next = current.children.get(ch);
    if (!next) { next = node(); current.children.set(ch, next); }
    current = next;
  }
  // Without this, "app" would exist the moment somebody indexed "apple".
  current.terminal = true;
}`,
  },
  {
    label: "Approach",
    body: "One walk does three jobs. descend follows the prefix and returns the node it lands on, or null. has is descend plus the flag, startsWith is descend alone, and suggest is a depth-first walk below that node with the children visited in sorted key order.",
    code: `private descend(prefix: string): Node | null {
  let current = this.root;
  for (const ch of prefix) {
    const next = current.children.get(ch);
    if (!next) return null;
    current = next;
  }
  return current;
}

has(phrase: string)      { const n = this.descend(phrase); return n !== null && n.terminal; }
startsWith(prefix: string) { return this.descend(prefix) !== null; }

// suggest: start AT the prefix node, not below it, or a prefix that is itself a
// phrase vanishes from its own suggestions.
const walk = (current: Node, built: string): void => {
  if (out.length >= limit) return;
  if (current.terminal) { out.push(built); if (out.length >= limit) return; }
  for (const ch of [...current.children.keys()].sort()) {
    walk(current.children.get(ch)!, built + ch);
    if (out.length >= limit) return;
  }
};`,
  },
  {
    label: "The trap",
    body: "Visiting children in sorted key order is what makes the results come out sorted with no sort at the end, and it is why the order does not depend on how the index was built. Stop the walk the instant limit is reached, or a prefix with ten thousand completions costs you all ten thousand, which is the only reason to build a trie at all.",
  },
];

export const prefixAutocomplete: Drill = {
  id: "prefix-autocomplete",
  track: "typescript",
  title: "Autocomplete over a prefix tree",
  pattern: "trie-prefix",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  statement: `A search box suggests as you type, over a few hundred thousand indexed phrases, and it has to answer between keystrokes.

\`\`\`ts
class Autocomplete {
  insert(phrase: string): void;
  has(phrase: string): boolean;                          // exact
  startsWith(prefix: string): boolean;                   // any phrase begins with it
  suggest(prefix: string, limit: number): string[];      // ascending, at most limit
}
\`\`\`

## Requirements

- **\`has\` is exact and \`startsWith\` is not.** Indexing \`"apple"\` means \`startsWith("app")\` is true and \`has("app")\` is false.
- **\`suggest\` returns at most \`limit\`**, in ascending order, and includes the prefix itself when it is a phrase.
- **Order never depends on insertion order.** Duplicates are indexed once.
- **An empty prefix means everything**; a limit of zero or less means nothing.
- **Matching is case sensitive.**

## Example

\`\`\`ts
const index = new Autocomplete();
["apple", "apply", "app", "apricot", "banana"].forEach((w) => index.insert(w));

index.suggest("ap", 10);   // ["app", "apple", "apply", "apricot"]
index.suggest("ap", 2);    // ["app", "apple"]
index.has("app");          // true
index.has("appl");         // false
index.startsWith("appl");  // true
\`\`\`

## Where this gets interesting

Keeping the phrases in an array and filtering on every keystroke is correct, and it is the answer that invites the follow-up about the other three hundred thousand phrases. A trie moves the cost from the size of the index to the length of what was typed: the walk to the prefix is as long as the prefix, and what hangs below it is exactly the answer.

Two details do most of the work. Visiting each node's children in key order means the results arrive sorted, with no sort at the end. And stopping the walk the instant \`limit\` is reached is what makes a prefix with ten thousand completions cost the ten you asked for, which is the entire reason to build this rather than filter an array.

One of the cases builds its own index of random phrases and asks for a random prefix, comparing your answer against filtering every phrase and sorting the survivors. The index is different each time this drill comes round.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "insert and has are O(length of the phrase). suggest is O(length of the prefix) plus the nodes visited to collect limit results, rather than anything proportional to the size of the index.",
};
